import { useEffect, useRef, useState } from 'react';
import {
  Bell,
  Building2,
  ChevronDown,
  HelpCircle,
  LogOut,
  Search,
  Settings as SettingsIcon,
  User as UserIcon,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../../hooks/useAuth.js';
import { useDemoMode } from '../../contexts/DemoModeContext.jsx';
import { openSupportChat } from './SupportChat.jsx';

const ROLE_LABELS = {
  team_leader: 'Team Leader',
  agent: 'Agent',
  isa: 'ISA',
  super_admin: 'Super Admin',
};

// Outlined role badge — matches the brand accent.
const ROLE_CHIP =
  'inline-flex items-center rounded-[22px] border-2 border-accent/40 text-accent px-3 py-0.5 text-xs font-semibold';

export default function Header() {
  const navigate = useNavigate();
  const isDemo = useDemoMode();
  const { user, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  const initials = (user.name || 'U')
    .split(' ')
    .map((p) => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  // Close the dropdown when clicking outside or pressing Escape.
  useEffect(() => {
    if (!menuOpen) return;
    const onDocClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  const onLogout = () => {
    if (typeof logout === 'function') logout();
    navigate('/login', { replace: true });
  };

  const go = (path) => {
    setMenuOpen(false);
    navigate(path);
  };

  const onHelp = () => {
    setMenuOpen(false);
    openSupportChat();
  };

  return (
    <header className="h-[60px] shrink-0 w-full bg-bg-topnav flex items-center px-6 gap-6">
      {/* Brand */}
      <div className="flex items-center gap-2 shrink-0">
        <div className="h-8 w-8 rounded-btn bg-accent/15 flex items-center justify-center text-accent shrink-0">
          <Building2 size={18} />
        </div>
        <div className="leading-tight">
          <div className="text-sm font-semibold text-white">
            ATLACIS Software
          </div>
          <div className="text-[11px] text-[#AAACBA]">Command Center</div>
        </div>
      </div>

      {/* Search */}
      <div
        className="shrink-0"
        style={{ marginLeft: '48px', width: '380px' }}
      >
        <div className="relative">
          <Search
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted"
          />
          <input
            type="search"
            placeholder="Search leads, properties, conversations…"
            style={{ fontSize: '15px' }}
            className="w-full h-10 pl-9 pr-3 rounded-[2px] bg-white border border-border-input
                       text-text-primary placeholder:text-text-muted
                       focus:outline-none focus:border-accent
                       focus:ring-1 focus:ring-accent/30 transition-colors"
          />
        </div>
      </div>

      {/* Right: bell + profile dropdown */}
      <div className="flex items-center gap-4 ml-auto">
        <button
          type="button"
          className="relative h-9 w-9 rounded-[4px] text-[#AAACBA] hover:text-white hover:bg-white/10 flex items-center justify-center transition-colors"
          aria-label="Notifications"
        >
          <Bell size={18} />
          <span className="absolute top-1.5 right-1.5 h-1.5 w-1.5 rounded-full bg-hot" />
        </button>

        <span className={ROLE_CHIP}>
          {ROLE_LABELS[user.role] || user.role}
        </span>

        {/* Profile button + dropdown */}
        <div className="relative" ref={menuRef}>
          <button
            type="button"
            onClick={() => setMenuOpen((o) => !o)}
            className="flex items-center gap-2 h-10 pl-2 pr-2 rounded-btn hover:bg-white/10 transition-colors"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
          >
            <div className="text-right leading-tight hidden sm:block">
              <div className="text-sm font-medium text-white">
                {user.name}
              </div>
              <div className="text-xs text-[#AAACBA]">{user.email}</div>
            </div>
            <div className="h-8 w-8 rounded-full bg-accent text-white flex items-center justify-center text-sm font-semibold shrink-0">
              {initials}
            </div>
            <ChevronDown
              size={14}
              className={`text-[#AAACBA] transition-transform ${
                menuOpen ? 'rotate-180' : ''
              }`}
            />
          </button>

          {menuOpen && (
            <div
              role="menu"
              className="absolute right-0 top-[48px] w-[240px] bg-white rounded-card shadow-card
                         border border-border-card overflow-hidden z-40"
            >
              <div className="px-4 py-3 border-b border-border-card">
                <div className="text-sm font-semibold text-text-primary truncate">
                  {user.name}
                </div>
                <div className="text-xs text-text-secondary truncate">
                  {user.email}
                </div>
              </div>
              <MenuItem
                icon={<UserIcon size={16} />}
                label="My Profile"
                onClick={() => go('/profile')}
              />
              <MenuItem
                icon={<SettingsIcon size={16} />}
                label="Account Settings"
                onClick={() => go('/re/settings')}
              />
              <MenuItem
                icon={<HelpCircle size={16} />}
                label="Help"
                onClick={onHelp}
              />
              <div className="border-t border-border-card">
                <MenuItem
                  icon={<LogOut size={16} />}
                  label={isDemo ? 'Exit demo' : 'Log out'}
                  onClick={onLogout}
                  danger
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

function MenuItem({ icon, label, onClick, danger = false }) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left transition-colors ${
        danger
          ? 'text-[#DC2626] hover:bg-[#FEF2F2]'
          : 'text-text-primary hover:bg-bg-subtle'
      }`}
    >
      <span
        className={`shrink-0 ${danger ? 'text-[#DC2626]' : 'text-text-secondary'}`}
      >
        {icon}
      </span>
      <span className="flex-1">{label}</span>
    </button>
  );
}
